import React from 'react';

const SelectedBundleDisplay = ({ 
    selectedBundle, 
    bundleItems, 
    teamPrimaryColor, 
    bundleStatus, 
    onRemoveBundle, 
    onOptionChange, 
    order,
    hasExtrasOutsideBundle = false,
    showIndividualItems = false,
    onToggleIndividualItems
}) => {
    if (!selectedBundle || !bundleItems || bundleItems.length === 0) {
        return null;
    }

    const bundleTotal = bundleItems.reduce((total, item) => total + (item.price * item.quantity), 0);

    // Helper function to get available options for each bundle item
    const getAvailableOptionsForBundleEntry = (bundleEntry) => {
        if (!order || !order.fundraiserOrderItems) return [];
        
        // Find the original bundle option to get the minimum price
        let originalOption = null;
        for (const item of order.fundraiserOrderItems) {
            const foundOption = item.fundraiserOrderItemOptions.find(
                option => option.itemOptionId === bundleEntry.itemsOptionId
            );
            if (foundOption) {
                originalOption = foundOption;
                break;
            }
        }
        
        const minimumPrice = originalOption ? originalOption.price : 0;
        
        // Find all options that could replace this bundle entry
        // Only show options that cost >= the original option price
        const allOptions = [];
        order.fundraiserOrderItems.forEach(item => {
            item.fundraiserOrderItemOptions.forEach(option => {
                // Only include options that are same price or higher
                if (option.price >= minimumPrice) {
                    allOptions.push({
                        itemOptionId: option.itemOptionId,
                        name: option.name,
                        price: option.price,
                        itemId: item.itemId
                    });
                }
            });
        });
        
        return allOptions;
    };

    // Get current selected option for a bundle entry
    const getCurrentSelectedOption = (bundleEntry) => {
        if (!order || !order.fundraiserOrderItems) return null;
        
        for (const item of order.fundraiserOrderItems) {
            const matchingOption = item.fundraiserOrderItemOptions.find(
                option => option.itemOptionId === bundleEntry.itemsOptionId
            );
            if (matchingOption) {
                return matchingOption;
            }
        }
        return null;
    };
    
    // Determine status colors and icons
    const getStatusInfo = () => {
        switch (bundleStatus) {
            case 'incomplete':
                return {
                    icon: '⚠️',
                    text: 'Bundle Incomplete',
                    borderColor: '#ffc107',
                    backgroundColor: '#fff3cd',
                    badgeColor: '#ffc107'
                };
            case 'complete':
            default:
                // Always use green checkmark style - never show blue present icon
                return {
                    icon: '✅',
                    text: 'Bundle Complete',
                    borderColor: '#28a745',
                    backgroundColor: '#d4edda',
                    badgeColor: '#28a745'
                };
        }
    };
    
    const statusInfo = getStatusInfo();

    return (
        <div style={{
            backgroundColor: statusInfo.backgroundColor,
            border: `2px solid ${statusInfo.borderColor}`,
            borderRadius: '10px',
            padding: '1.5rem',
            marginBottom: '2rem',
            transition: 'all 0.3s ease'
        }}>
            <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap">
                <h4 style={{ color: statusInfo.borderColor, margin: 0, marginBottom: '0.5rem' }}>
                    {statusInfo.icon} {selectedBundle.name}
                </h4>
                <div className="d-flex gap-2 align-items-center flex-wrap">
                    <span className="badge fs-6" style={{ backgroundColor: statusInfo.badgeColor, color: 'white' }}>
                        {statusInfo.text}
                    </span>
                    <span className="badge bg-secondary fs-6">
                        Bundle Total: ${bundleTotal.toFixed(2)}
                    </span>
                    <button 
                        className="btn btn-sm btn-outline-danger"
                        onClick={onRemoveBundle}
                        title="Remove Bundle"
                        style={{
                            whiteSpace: 'nowrap',
                            fontSize: window.innerWidth < 768 ? '0.75rem' : '0.875rem',
                            padding: window.innerWidth < 768 ? '0.25rem 0.5rem' : '0.375rem 0.75rem'
                        }}
                    >
                        🗑️ Remove
                    </button>
                </div>
            </div>
            
            <div className="row g-3">
                {selectedBundle.optionBundleEntries?.map((bundleEntry, index) => {
                    const currentOption = getCurrentSelectedOption(bundleEntry);
                    const availableOptions = getAvailableOptionsForBundleEntry(bundleEntry);
                    
                    return (
                        <div key={index} className="col-md-6">
                            <div className="card card-body" style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}>
                                <div className="mb-2">
                                    <small className="text-muted">{currentOption?.name || 'Select an option'}:</small>
                                    <br />
                                    <span className="fw-bold">Quantity: {bundleEntry.quantity}</span>
                                </div>
                                
                                <div className="mb-2">
                                    <label className="form-label small">Choose Option:</label>
                                    <select 
                                        className="form-select form-select-sm"
                                        value={currentOption?.itemOptionId || ''}
                                        onChange={(e) => onOptionChange(bundleEntry, e.target.value)}
                                    >
                                        {availableOptions.map(option => (
                                            <option key={option.itemOptionId} value={option.itemOptionId}>
                                                {option.name} - ${option.price}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="text-end">
                                    <span className="badge bg-success">
                                        ${((currentOption?.price || 0) * bundleEntry.quantity).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            
            <div className="mt-3 text-center">
                <div className="text-muted" style={{ fontSize: '1.75rem' }}>
                    {hasExtrasOutsideBundle ? (
                        <>
                            Your sponsorship is complete and you have additional items in your purchase.
                        </>
                    ) : (
                        <>
                            Perfect! Your sponsorship is complete. You can still add more items to increase your impact.
                        </>
                    )}
                    
                    {/* Always show the toggle button when bundle is selected and toggle function is available */}
                    {onToggleIndividualItems && (
                        <button 
                            type="button"
                            className="btn btn-link btn-sm p-1 ms-2"
                            onClick={onToggleIndividualItems}
                            style={{ 
                                color: teamPrimaryColor || '#0e456c',
                                textDecoration: showIndividualItems ? 'underline' : 'none',
                                fontSize: '1.2rem'
                            }}
                        >
                            {showIndividualItems ? 'Hide items' : 'Show items'}
                        </button>
                    )}
                    
                    {bundleStatus === 'incomplete' && 'Some bundle items have been removed. Add them back to complete your bundle.'}
                </div>
            </div>
        </div>
    );
};

export default SelectedBundleDisplay;
